'use client';

import { useCallback, useEffect, useState } from 'react';

/**
 * Posledne vyhladavane lokality v localStorage.
 *
 * Ukladame len minimum potrebne na opatovny vyber (id/name/slug/type), aby
 * sa dala polozka rovno pouzit ako hodnota filtra bez volania API.
 */
const STORAGE_KEY = 'realico:recent-locations';
const MAX_RECENT = 6;

export type RecentLocation = {
    id?: string;
    name: string;
    slug: string;
    type?: string;
};

function isValid(value: unknown): value is RecentLocation {
    if (!value || typeof value !== 'object') return false;
    const v = value as Record<string, unknown>;
    return typeof v.name === 'string' && typeof v.slug === 'string' && !!v.name && !!v.slug;
}

/** Precita zoznam; pri akejkolvek chybe (private mode, poskodene data) vrati []. */
export function readRecentLocations(): RecentLocation[] {
    if (typeof window === 'undefined') return [];
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.filter(isValid).slice(0, MAX_RECENT);
    } catch {
        return [];
    }
}

/** Prida lokalitu na zaciatok, deduplikuje podla slug a orezhe na MAX_RECENT. */
export function writeRecentLocation(location: RecentLocation): RecentLocation[] {
    if (typeof window === 'undefined') return [];
    if (!isValid(location)) return readRecentLocations();

    const entry: RecentLocation = {
        id: location.id,
        name: location.name,
        slug: location.slug,
        type: location.type,
    };
    const next = [entry, ...readRecentLocations().filter((i) => i.slug !== entry.slug)].slice(
        0,
        MAX_RECENT,
    );

    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
        // storage plny alebo zakazany - historia je len komfort, nie kriticka
    }
    return next;
}

export function clearRecentLocations(): void {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.removeItem(STORAGE_KEY);
    } catch {
        // ignoruj
    }
}

/**
 * Nacitava az po mounte - localStorage nie je dostupny pri SSR a citanie
 * pocas renderu by sposobilo hydration mismatch.
 */
export function useRecentLocations() {
    const [recent, setRecent] = useState<RecentLocation[]>([]);

    useEffect(() => {
        setRecent(readRecentLocations());
    }, []);

    const remember = useCallback((location: RecentLocation) => {
        setRecent(writeRecentLocation(location));
    }, []);

    const clear = useCallback(() => {
        clearRecentLocations();
        setRecent([]);
    }, []);

    return { recent, remember, clear };
}
